import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import SectionShell from '../SectionShell';
import RevealText from '../RevealText';

// TEST (Juni 2026): fallende Diamanten statt Original-Hero.
// REVERT: HERO_VIDEO_MODE auf 'original' stellen.
const HERO_VIDEO_MODE = 'test'; // 'original' | 'test'
const HERO_SRC = HERO_VIDEO_MODE === 'test'
  ? '/videos/hero-falling-diamonds-test.mp4'
  : '/videos/hero-diamond.mp4';
const HERO_POSTER = HERO_VIDEO_MODE === 'test' ? undefined : '/videos/hero-diamond-poster.jpg';

export default function SectionHero() {
  const { t, lang } = useT();
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  // Fade the video in once it starts playing.
  const [videoReady, setVideoReady] = useState(false);
  useEffect(() => {
    const el = heroRef.current;
    const v = videoRef.current;
    if (!el || !v) return undefined;

    // Pause the video when the hero scrolls off-screen to save decode cycles.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: [0, 0.05, 0.2] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Split hero title into 3 dramatic lines based on translation length.
  const heroLines = {
    de: ['EINE', 'EINZIGARTIGE', 'MÖGLICHKEIT'],
    en: ['A ONCE-IN-A-', 'GENERATION', 'OPPORTUNITY'],
    es: ['UNA', 'OPORTUNIDAD', 'ÚNICA'],
    it: ["UN'OPPORTUNITÀ", 'UNICA', ''],
    pt: ['UMA', 'OPORTUNIDADE', 'ÚNICA'],
    pl: ['WYJĄTKOWA', 'MOŻLIWOŚĆ', ''],
    cs: ['JEDINEČNÁ', 'PŘÍLEŽITOST', ''],
    sk: ['JEDINEČNÁ', 'PRÍLEŽITOSŤ', ''],
    hu: ['EGY', 'EGYEDÜLÁLLÓ', 'LEHETŐSÉG'],
    ro: ['O', 'OPORTUNITATE', 'UNICĂ'],
    sr: ['JEDINSTVENA', 'PRILIKA', ''],
    tr: ['EŞSİZ', 'BİR', 'FIRSAT'],
    fr: ['UNE', 'OPPORTUNITÉ', 'UNIQUE'],
    nl: ['EEN', 'UNIEKE', 'KANS'],
    sv: ['EN', 'UNIK', 'MÖJLIGHET'],
    bg: ['ЕДНА', 'УНИКАЛНА', 'ВЪЗМОЖНОСТ'],
    el: ['ΜΙΑ', 'ΜΟΝΑΔΙΚΗ', 'ΕΥΚΑΙΡΙΑ'],
    sl: ['EDINSTVENA', 'PRILOŽNOST', ''],
    sq: ['NJË', 'MUNDËSI', 'UNIKE'],
    mk: ['ЕДИНСТВЕНА', 'МОЖНОСТ', ''],
    uk: ['УНІКАЛЬНА', 'МОЖЛИВІСТЬ', ''],
    ru: ['УНИКАЛЬНАЯ', 'ВОЗМОЖНОСТЬ', ''],
    mt: ['OPPORTUNITÀ', 'UNIKA', ''],
    lb: ['ENG', 'EENZEGAARTEG', 'MÉIGLECHKEET'],
    ar: ['فرصة', 'فريدة', 'من نوعها'],
    zh: ['一个', '独特的', '机会'],
    ja: ['唯一無二の', '機会', ''],
    ko: ['독보적인', '기회', ''],
    hi: ['एक', 'अद्वितीय', 'अवसर'],
  };
  const lines = (heroLines[lang] || heroLines.en).filter(Boolean);

  return (
    <SectionShell id="hero" data-section-label="Hero" fullBleed>
      <div ref={heroRef} className="relative w-full h-screen overflow-hidden hero-bg">
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            className="w-full h-full object-cover hero-video"
            style={{
              opacity: 1,
              transition: 'opacity 1200ms ease-out',
            }}
            src={HERO_SRC}
            poster={HERO_POSTER}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            data-testid="hero-video"
            onCanPlay={() => setVideoReady(true)}
            onPlaying={() => setVideoReady(true)}
            onError={(e) => {
              // Fallback: keep video element visible with poster; log for debugging.
              console.warn('[hero-video] error', e?.target?.error);
            }}
          />
        </div>
        {/* Subtle vignette — dark only at edges, earth stays bright in the middle */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 65% at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 55%, rgba(6,10,20,0.35) 100%)',
          }}
        />
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 lg:px-10 text-center pointer-events-none">
          <div className="hero-copy-box">
          <h1
            data-testid="hero-title"
            className="stig-headline stig-headline--hero mb-10 uppercase hero-title-on-video w-full"
            style={{ display: 'block' }}
          >
            {lines.map((ln, i) => (
              <RevealText
                key={`${lang}-${i}-${ln}`}
                as="span"
                delay={i * 220 + 1700}
                stagger={32}
                className="block"
              >
                {ln}
              </RevealText>
            ))}
          </h1>

          <p
            className="font-ui text-white uppercase mb-8 font-light hero-subtitle-on-video w-full"
            style={{ letterSpacing: 'clamp(0.08em, 0.6vw, 0.32em)', fontSize: 'clamp(1.4rem, 3.4vw, 3.6rem)', whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word', lineHeight: 1.18 }}
          >
            {t.hero.subtitle}
          </p>

          <p
            className="font-ui text-white leading-tight font-light hero-description-on-video w-full"
            style={{ fontSize: 'clamp(1.6rem, 2.6vw, 3rem)', maxWidth: '90ch' }}
          >
            {t.hero.description}
          </p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
